<template>
  <div class="sh-page">
    <!-- Header -->
    <header class="sh-header">
      <button class="sh-header__btn" @click="$router.go(-1)">
        <van-icon name="arrow-left" size="20" color="#fff" />
      </button>
      <h1 class="sh-header__title">{{ $lang.season_history_title || 'Season History' }}</h1>
      <div class="sh-header__btn" style="visibility:hidden;"></div>
    </header>

    <div v-if="loading" class="sh-loading">
      <van-loading color="#ff5a1f" size="32" />
    </div>

    <template v-else>
    <div v-if="readError" class="sh-read-error">
      {{ readError }}
    </div>

    <!-- Current Season Summary -->
    <section class="sh-current">
      <div class="sh-current__badge">{{ $lang.season_history_current || 'CURRENT' }}</div>
      <div class="sh-current__card">
        <div class="sh-current__info">
          <h3 class="sh-current__name">{{ currentSeason.name }}</h3>
          <p class="sh-current__date">{{ currentSeason.dateRange }}</p>
        </div>
        <div class="sh-current__progress">
          <div class="sh-current__pts">
            <span class="sh-current__pts-val">{{ currentSeason.points }}</span>
            <span class="sh-current__pts-label">PTS</span>
          </div>
          <div class="sh-current__bar">
            <div class="sh-current__fill" :style="{ width: currentSeason.progress + '%' }"></div>
          </div>
          <p class="sh-current__target">{{ $lang.season_history_target || 'Target' }}: {{ currentSeason.target }} PTS</p>
        </div>
        <div class="sh-current__rewards">
          <div class="sh-current__reward-item">
            <div class="sh-coin-mini"><span>G</span></div>
            <div>
              <p class="sh-current__reward-val">{{ currentSeason.basicClaimed }}/{{ currentSeason.basicTotal }}</p>
              <p class="sh-current__reward-label">{{ $lang.season_basic || 'Basic' }}</p>
            </div>
          </div>
          <div class="sh-current__reward-item">
            <div class="sh-shield-mini"></div>
            <div>
              <p class="sh-current__reward-val">{{ currentSeason.juniorClaimed }}/{{ currentSeason.juniorTotal }}</p>
              <p class="sh-current__reward-label">{{ $lang.season_junior || 'Junior' }}</p>
            </div>
          </div>
          <div class="sh-current__reward-item">
            <div class="sh-chest-mini">
              <div class="sh-chest-mini__lid"></div>
              <div class="sh-chest-mini__lock"></div>
            </div>
            <div>
              <p class="sh-current__reward-val">{{ currentSeason.seniorClaimed }}/{{ currentSeason.seniorTotal }}</p>
              <p class="sh-current__reward-label">{{ $lang.season_senior || 'Senior' }}</p>
            </div>
          </div>
        </div>
        <button class="sh-current__btn" @click="$jumpTo('/seasonPass')">
          {{ $lang.season_history_view_current || 'View Current Season' }}
        </button>
      </div>
    </section>

    <!-- Past Seasons List -->
    <section class="sh-past">
      <h2 class="sh-past__title">
        <span class="sh-past__line"></span>
        {{ $lang.season_history_past || 'Past Seasons' }}
      </h2>

      <div class="sh-past__list">
        <div
          v-for="(season, idx) in pastSeasons"
          :key="idx"
          class="sh-season-card"
        >
          <div class="sh-season-card__top">
            <div>
              <h4 class="sh-season-card__name">{{ season.name }}</h4>
              <p class="sh-season-card__date">{{ season.dateRange }}</p>
            </div>
            <div class="sh-season-card__status" :class="'sh-season-card__status--' + season.status">
              {{ season.status === 'completed' ? ($lang.season_history_completed || 'COMPLETED') : ($lang.season_history_expired || 'EXPIRED') }}
            </div>
          </div>

          <!-- Progress -->
          <div class="sh-season-card__progress">
            <div class="sh-season-card__bar">
              <div class="sh-season-card__fill" :style="{ width: season.progress + '%' }"></div>
            </div>
            <span class="sh-season-card__pts">{{ season.points }}/{{ season.target }} PTS</span>
          </div>

          <!-- Rewards Summary -->
          <div class="sh-season-card__rewards">
            <div class="sh-season-card__reward">
              <div class="sh-coin-mini"><span>G</span></div>
              <span>{{ season.basicClaimed }}/{{ season.basicTotal }}</span>
            </div>
            <div class="sh-season-card__reward">
              <div class="sh-shield-mini"></div>
              <span>{{ season.juniorClaimed }}/{{ season.juniorTotal }}</span>
            </div>
            <div class="sh-season-card__reward">
              <div class="sh-chest-mini">
                <div class="sh-chest-mini__lid"></div>
                <div class="sh-chest-mini__lock"></div>
              </div>
              <span>{{ season.seniorClaimed }}/{{ season.seniorTotal }}</span>
            </div>
            <div class="sh-season-card__total">
              <van-icon name="gold-coin-o" size="14" color="#ffb020" />
              <span>+{{ season.totalGold }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="pastSeasons.length === 0" class="sh-empty">
        <van-icon name="records" size="48" color="var(--vk-t3)" />
        <p>{{ $lang.season_history_empty || 'No past seasons yet' }}</p>
      </div>
    </section>
    </template>
  </div>
</template>

<script>
import { TaskCenterWeeklyInit } from '@/api/common'
import { failMessage as rewardFailMessage, hasServerProvenEmptyRead, hasUnavailableMarker, isNeedLogin, isOkResponse, responseContent } from './rewardApi'

export default {
  name: 'SeasonHistory',
  data() {
    return {
      loading: true,
      currentSeason: {
        name: this.$lang.season_pass_title || 'Season Pass',
        dateRange: '',
        points: 0,
        target: 0,
        progress: 0,
        basicClaimed: 0,
        basicTotal: 0,
        juniorClaimed: 0,
        juniorTotal: 0,
        seniorClaimed: 0,
        seniorTotal: 0
      },
      pastSeasons: [],
      readError: ''
    }
  },
  mounted() {
    this.fetchHistory()
  },
  methods: {
    safeNumber(value, fallback = 0) {
      const n = Number(typeof value === 'string' ? value.replace(/,/g, '') : value)
      return Number.isFinite(n) ? n : fallback
    },
    safeArray(value) {
      return Array.isArray(value) ? value : []
    },
    firstArray(source, keys) {
      if (!source) return []
      for (const key of keys) {
        const value = source[key]
        if (Array.isArray(value)) return value
        if (value && typeof value === 'object') {
          const nested = value.list || value.records || value.dataList || value.pageList || value.items || value.rows
          if (Array.isArray(nested)) return nested
        }
      }
      return []
    },
    firstValue(source, keys, fallback = '') {
      if (!source) return fallback
      for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') return source[key]
      }
      return fallback
    },
    requireLogin() {
      if (this.$bus) this.$bus.$emit('openLogin')
    },
    failMessage(res, fallback) {
      return rewardFailMessage(res, fallback || this.$lang.mc_claim_failed || 'This feature is temporarily unavailable. Please try again later.')
    },
    preserveReadError(res, fallback) {
      const message = this.failMessage(res, fallback || this.$lang.reward_no_data || 'No rewards found')
      this.readError = `读取失败，已保留已加载数据。${message}`
      this.$toast({ message: this.readError, icon: 'cross' })
    },
    isServerProvenEmptyRead(res) {
      return typeof hasServerProvenEmptyRead === 'function' && hasServerProvenEmptyRead(res)
    },
    isFalseValue(value) {
      if (value === false || value === 0) return true
      const normalized = String(value == null ? '' : value).trim().toLowerCase()
      return ['false', '0', 'no', 'disabled', 'read_only', 'read-only', 'write_blocked', 'write-blocked'].includes(normalized)
    },
    isTrueValue(value) {
      if (value === true || value === 1) return true
      const normalized = String(value == null ? '' : value).trim().toLowerCase()
      return ['true', '1', 'yes', 'claimable', 'available'].includes(normalized)
    },
    hasRewardGuardMarker(source, depth = 0) {
      if (!source || typeof source !== 'object' || depth > 5) return false
      if (hasUnavailableMarker(source)) return true
      const blockingKeys = ['readOnly', 'readOnlyMode', 'readonly', 'read_only', 'read_only_mode', 'mock', 'mockMode', 'mock_mode', 'mockFallback', 'mock_fallback', 'stub', 'unimplemented', 'notImplemented', 'not_implemented', 'notSupported', 'not_supported', 'unsupported', 'failed', 'failClosed', 'fail_closed', 'writeBlocked', 'write_blocked', 'fallback', 'fallbackUsed', 'fallback_used', 'dataUnavailable', 'data_unavailable', 'readFailure', 'read_failure', 'readUnavailable', 'read_unavailable', 'routeUnavailable', 'route_unavailable', 'technicalError', 'technical_error', 'technicalMarker', 'technical_marker', 'technicalException', 'technical_exception', 'backendFailure', 'backend_failure', 'notConnected', 'not_connected', 'falseProof', 'false_proof']
      const blockingTokens = ['mock', 'mock_mode', 'mock mode', 'mock-mode', 'mockmode', 'mock_fallback', 'read_only', 'read_only_mode', 'read only mode', 'read-only-mode', 'readonlymode', 'readonly', 'read only', 'read-only', 'stub', 'unimplemented', 'not_implemented', 'not_supported', 'not supported', 'unsupported', 'failed', 'failclosed', 'fail_closed', 'fail closed', 'fail-closed', 'write_blocked', 'writeblocked', 'write blocked', 'write-blocked', 'fallback', 'fallback_used', 'fallback used', 'fallback-used', 'data_unavailable', 'data unavailable', 'data-unavailable', 'dataunavailable', 'readfailure', 'read_failure', 'read failure', 'read-failure', 'readunavailable', 'read_unavailable', 'read unavailable', 'read-unavailable', 'routeunavailable', 'route_unavailable', 'route unavailable', 'route-unavailable', 'technical_error', 'technical error', 'technical-error', 'technicalmarker', 'technical_marker', 'technical marker', 'technical-marker', 'technical_exception', 'technical exception', 'technical-exception', 'backendfailure', 'backend_failure', 'backend failure', 'backend-failure', '技术错误', '技术异常']
      const isActive = value => value === true || value === 1 || blockingTokens.some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      if (blockingKeys.some(key => isActive(source[key]))) return true
      if ([source.status, source.state, source.result, source.code, source.errorCode, source.msg, source.message, source.reason, source.desc, source.description].some(isActive)) return true
      return ['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details'].some(key => this.hasRewardGuardMarker(source[key], depth + 1))
    },
    isRewardUnavailable(reward) {
      if (!reward) return true
      return this.hasRewardGuardMarker(reward) ||
        this.isFalseValue(reward.rewardWritable) ||
        this.isFalseValue(reward.available) ||
        this.isTrueValue(reward.locked)
    },
    extractSeasonContent(content) {
      if (!content) return {}
      return content.season ||
        content.seasonPass ||
        content.weekly ||
        content.data ||
        content
    },
    formatDateRange(source) {
      const direct = this.firstValue(source, ['dateRange', 'range', 'period'], '')
      if (direct) return String(direct)
      const start = this.firstValue(source, ['startDate', 'beginDate', 'from', 'startTime'], '')
      const end = this.firstValue(source, ['endDate', 'finishDate', 'to', 'endTime'], '')
      const aliasedStart = start || this.firstValue(source, ['beginTime', 'begin_time', 'start_time', 'startedAt', 'createdAt'], '')
      const aliasedEnd = end || this.firstValue(source, ['finishTime', 'finish_time', 'end_time', 'endedAt', 'expireTime', 'expiredAt'], '')
      if (!aliasedStart && !aliasedEnd) return ''
      return `${this.formatShortDate(aliasedStart)} - ${this.formatShortDate(aliasedEnd)}`
    },
    formatShortDate(value) {
      if (!value) return ''
      const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/)
      const dt = dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(value)
      if (isNaN(dt.getTime())) return String(value)
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${monthNames[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`
    },
    rewardStatus(reward, points, currentPoints) {
      void points
      void currentPoints
      if (!reward) return 'locked'
      const raw = String(this.firstValue(reward, ['status', 'state', 'rewardStatus'], '')).toLowerCase()
      if (reward.claimed === true || reward.isClaimed === true || raw === 'claimed' || raw === 'completed' || raw === 'received') return 'claimed'
      if (this.isRewardUnavailable(reward)) return 'locked'
      if (
        reward.rewardWritable !== false &&
        reward.readOnly !== true &&
        (this.isTrueValue(reward.canClaim) || this.isTrueValue(reward.canClaimReward) || raw === 'claimable' || raw === 'available')
      ) return 'claimable'
      return 'locked'
    },
    normalizeMilestones(section) {
      const rows = this.firstArray(section, ['milestones', 'progressRewards', 'rewardTrack', 'rewards', 'list'])
      return rows.map(item => {
        const points = this.safeNumber(this.firstValue(item, ['points', 'threshold', 'point', 'targetPoints', 'activityPoints'], 0))
        if (points <= 0) return null
        return {
          points,
          basic: this.rewardStatus(item.basic || item.free || item.base || item, points),
          junior: this.rewardStatus(item.junior || item.silver || item.premium || {}, points),
          senior: this.rewardStatus(item.senior || item.gold || item.pro || {}, points),
          amount: this.safeNumber(this.firstValue(item, ['rewardAmount', 'rewardGold', 'amount', 'bonus'], 0))
        }
      }).filter(Boolean).sort((a, b) => a.points - b.points)
    },
    countRewards(milestones, tier) {
      const total = milestones.filter(item => item[tier] !== 'locked').length
      const claimed = milestones.filter(item => item[tier] === 'claimed').length
      return { total, claimed }
    },
    normalizeCurrent(section, milestones, fallbackTaskRows = []) {
      const sectionTaskRows = this.firstArray(section, ['taskList', 'task_list', 'tasks', 'taskRows', 'task_rows'])
      const taskRows = sectionTaskRows.length ? sectionTaskRows : fallbackTaskRows
      const rowPoints = taskRows.reduce((sum, item) => {
        return sum + this.safeNumber(this.firstValue(item, ['points', 'point', 'score', 'activityPoints', 'activity_points', 'rewardPoints', 'reward_points', 'claim_amount', 'claimAmount', 'rewardAmount', 'reward', 'amount'], 0))
      }, 0)
      const points = this.safeNumber(
        this.firstValue(section, ['currentPoints', 'points', 'point', 'score', 'activityPoints', 'activity_points', 'totalPoints'], rowPoints),
        rowPoints
      )
      const target = this.safeNumber(
        this.firstValue(section, ['target', 'targetPoints', 'maxPoints', 'max_points', 'maxPoint', 'max_point'], milestones.length ? milestones[milestones.length - 1].points : points),
        milestones.length ? milestones[milestones.length - 1].points : points
      )
      const basic = this.countRewards(milestones, 'basic')
      const junior = this.countRewards(milestones, 'junior')
      const senior = this.countRewards(milestones, 'senior')
      this.currentSeason = {
        name: this.firstValue(section, ['seasonName', 'name', 'title'], this.$lang.season_pass_title || 'Season Pass'),
        dateRange: this.formatDateRange(section),
        points,
        target,
        progress: target > 0 ? Math.min(100, Math.round((points / target) * 100)) : 0,
        basicClaimed: basic.claimed,
        basicTotal: basic.total,
        juniorClaimed: junior.claimed,
        juniorTotal: junior.total,
        seniorClaimed: senior.claimed,
        seniorTotal: senior.total
      }
    },
    normalizePastSeason(item, index) {
      const points = this.safeNumber(this.firstValue(item, ['points', 'point', 'score', 'currentPoints', 'activityPoints', 'activity_points', 'totalPoints', 'rewardPoints', 'reward_points'], 0))
      const target = this.safeNumber(this.firstValue(item, ['target', 'targetPoints', 'maxPoints', 'max_points', 'maxPoint', 'max_point'], points), points)
      return {
        name: this.firstValue(item, ['seasonName', 'name', 'title'], `${this.$lang.season_pass_title || 'Season'} ${index + 1}`),
        dateRange: this.formatDateRange(item),
        points,
        target,
        progress: target > 0 ? Math.min(100, Math.round((points / target) * 100)) : 0,
        status: String(this.firstValue(item, ['status', 'state'], 'expired')).toLowerCase() === 'completed' ? 'completed' : 'expired',
        basicClaimed: this.safeNumber(this.firstValue(item, ['basicClaimed', 'freeClaimed', 'claimedBasic'], 0)),
        basicTotal: this.safeNumber(this.firstValue(item, ['basicTotal', 'freeTotal', 'totalBasic'], 0)),
        juniorClaimed: this.safeNumber(this.firstValue(item, ['juniorClaimed', 'silverClaimed', 'claimedJunior'], 0)),
        juniorTotal: this.safeNumber(this.firstValue(item, ['juniorTotal', 'silverTotal', 'totalJunior'], 0)),
        seniorClaimed: this.safeNumber(this.firstValue(item, ['seniorClaimed', 'goldClaimed', 'claimedSenior'], 0)),
        seniorTotal: this.safeNumber(this.firstValue(item, ['seniorTotal', 'goldTotal', 'totalSenior'], 0)),
        totalGold: this.$formatNumberWithCommas(this.safeNumber(this.firstValue(item, ['totalGold', 'rewardGold', 'rewardAmount', 'awardAmount', 'giftAmount', 'bonusAmount', 'claim_amount', 'claimAmount', 'reward', 'amount', 'bonus'], 0)))
      }
    },
    buildPastFromClaimRows(rows) {
      const groups = {}
      rows.forEach(item => {
        const dateValue = this.firstValue(item, ['claim_time', 'claimTime', 'createDate', 'create_time', 'createdAt', 'date', 'time'], '')
        const dt = new Date(dateValue)
        if (isNaN(dt.getTime())) return
        const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
        if (!groups[key]) {
          groups[key] = {
            seasonName: key,
            dateRange: `${this.formatShortDate(new Date(dt.getFullYear(), dt.getMonth(), 1))} - ${this.formatShortDate(new Date(dt.getFullYear(), dt.getMonth() + 1, 0))}`,
            points: 0,
            target: this.currentSeason.target,
            status: 'completed',
            basicClaimed: 0,
            basicTotal: 0,
            juniorClaimed: 0,
            juniorTotal: 0,
            seniorClaimed: 0,
            seniorTotal: 0,
            totalGold: 0
          }
        }
        const amount = this.safeNumber(this.firstValue(item, ['claim_amount', 'claimAmount', 'rewardAmount', 'rewardGold', 'reward', 'amount', 'points', 'point', 'score'], 0))
        groups[key].points += amount
        groups[key].totalGold += amount
        groups[key].basicClaimed += 1
        groups[key].basicTotal += 1
      })
      return Object.keys(groups)
        .sort((a, b) => b.localeCompare(a))
        .map((key, index) => this.normalizePastSeason(groups[key], index))
    },
    applyHistory(content) {
      const section = this.extractSeasonContent(content)
      const sectionTaskRows = this.firstArray(section, ['taskList', 'task_list', 'tasks', 'taskRows', 'task_rows'])
      const contentTaskRows = section === content ? [] : this.firstArray(content, ['taskList', 'task_list', 'tasks', 'taskRows', 'task_rows'])
      const taskRows = sectionTaskRows.concat(contentTaskRows)
      const milestones = this.normalizeMilestones(section)
      this.normalizeCurrent(section, milestones, taskRows)
      const pastRows = this.firstArray(section, ['pastSeasons', 'seasonHistory', 'history', 'records', 'record'])
      const contentPastRows = pastRows.length ? pastRows : this.firstArray(content, ['pastSeasons', 'seasonHistory', 'history', 'records', 'record', 'list', 'dataList', 'items'])
      if (pastRows.length) {
        this.pastSeasons = pastRows.map((item, index) => this.normalizePastSeason(item, index))
      } else {
        this.pastSeasons = contentPastRows.length
          ? contentPastRows.map((item, index) => this.normalizePastSeason(item, index))
          : this.buildPastFromClaimRows(taskRows)
      }
    },
    hasReadableSeasonHistoryContent(content) {
      if (!content || typeof content !== 'object') return false
      const section = this.extractSeasonContent(content)
      const taskRows = this.firstArray(section, ['taskList', 'task_list', 'tasks', 'taskRows', 'task_rows'])
        .concat(section === content ? [] : this.firstArray(content, ['taskList', 'task_list', 'tasks', 'taskRows', 'task_rows']))
      const pastRows = this.firstArray(section, ['pastSeasons', 'seasonHistory', 'history', 'records', 'record'])
      const contentPastRows = this.firstArray(content, ['pastSeasons', 'seasonHistory', 'history', 'records', 'record', 'list', 'dataList', 'items'])
      if (taskRows.length || pastRows.length || contentPastRows.length) return true
      return [
        'seasonName',
        'name',
        'currentPoints',
        'points',
        'point',
        'score',
        'activityPoints',
        'activity_points',
        'totalPoints',
        'targetPoints',
        'target',
        'totalGold',
        'rewardGold',
        'startDate',
        'start_date',
        'endDate',
        'end_date'
      ].some(key => section[key] !== undefined && section[key] !== null && section[key] !== '')
    },
    async fetchHistory() {
      this.loading = true
      try {
        const res = await TaskCenterWeeklyInit({})
        if (isNeedLogin(res)) {
          this.requireLogin()
        } else if (this.isServerProvenEmptyRead(res)) {
          this.readError = ''
          this.applyHistory(responseContent(res))
        } else if (this.hasRewardGuardMarker(res)) {
          this.preserveReadError(res)
        } else if (isOkResponse(res)) {
          const content = responseContent(res)
          if (!this.hasReadableSeasonHistoryContent(content)) {
            this.preserveReadError(res)
            return
          }
          this.readError = ''
          this.applyHistory(content)
        } else {
          if (res && !isOkResponse(res)) {
            this.preserveReadError(res)
          }
        }
      } catch (e) {
        this.preserveReadError(e, '活动请求失败，请稍后重试。')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
@primary: @brand;   /* 绿→火焰橙(强调/激活) */
@gold: #ffb020;     /* 黄→金(金额/奖) */
@silver: #C0C0C0;   /* 银(段位,中性保留) */
@bg-dark: @vk-bg;   /* 页面底 */
@surface: rgba(255, 255, 255, 0.05);

.sh-page {
  min-height: 100vh;
  background: @bg-dark;
  color: #e2e8f0;
  padding-bottom: 40px;
}

.sh-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
}

.sh-read-error {
  margin: 12px 16px 0;
  padding: 10px 12px;
  border: 1px solid fade(#f5455c, 35%);
  border-radius: 8px;
  background: fade(#f5455c, 10%);
  color: #fecaca;
  font-size: var(--vk-fs-xs);
  line-height: 1.5;
}

// ====== HEADER ======
.sh-header {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: fade(@bg-dark, 80%);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &__btn {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    border-radius: 50%;

    &:active {
      background: rgba(255, 255, 255, 0.05);
    }
  }

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 800;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #f1f5f9;
  }
}

// ====== CURRENT SEASON ======
.sh-current {
  padding: 16px;
  position: relative;

  &__badge {
    display: inline-block;
    background: @primary;
    color: @bg-dark;
    font-size: var(--vk-fs-2xs);
    font-weight: 900;
    letter-spacing: 0.15em;
    padding: 4px 12px;
    border-radius: 999px;
    margin-bottom: 12px;
    box-shadow: 0 0 10px fade(@primary, 50%);
  }

  &__card {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.08), fade(@primary, 5%));
    border: 1px solid fade(@primary, 20%);
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 0 20px fade(@primary, 10%);
  }

  &__info {
    margin-bottom: 16px;
  }

  &__name {
    font-size: var(--vk-fs-md);
    font-weight: 800;
    color: #f1f5f9;
    letter-spacing: 0.02em;
  }

  &__date {
    font-size: var(--vk-fs-xs);
    color: var(--vk-t2);
    margin-top: 4px;
  }

  &__progress {
    margin-bottom: 16px;
  }

  &__pts {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 8px;

    &-val {
      font-size: 28px;
      font-weight: 900;
      color: @primary;
      font-style: italic;
    }

    &-label {
      font-size: var(--vk-fs-xs);
      color: #cbd5e1;
    }
  }

  &__bar {
    height: 10px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 999px;
    overflow: hidden;
    padding: 2px;
  }

  &__fill {
    height: 100%;
    background: @primary;
    border-radius: 999px;
    box-shadow: 0 0 8px fade(@primary, 50%);
    transition: width 0.5s;
  }

  &__target {
    font-size: var(--vk-fs-2xs);
    color: var(--vk-t3);
    margin-top: 6px;
    text-align: right;
  }

  &__rewards {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    padding: 12px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  &__reward-item {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__reward-val {
    font-size: var(--vk-fs-base);
    font-weight: 800;
    color: #f1f5f9;
  }

  &__reward-label {
    font-size: 9px;
    color: var(--vk-t3);
    text-transform: uppercase;
    font-weight: 700;
  }

  &__btn {
    width: 100%;
    padding: 12px;
    background: @primary;
    color: @bg-dark;
    font-size: var(--vk-fs-xs);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    box-shadow: 0 0 15px fade(@primary, 40%);

    &:active {
      opacity: 0.9;
      transform: scale(0.98);
    }
  }
}

// ====== PAST SEASONS ======
.sh-past {
  padding: 0 16px;
  margin-top: 24px;

  &__title {
    font-size: var(--vk-fs-sm);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: #f1f5f9;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
  }

  &__line {
    display: inline-block;
    width: 32px;
    height: 1px;
    background: @primary;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}

// ====== SEASON CARD ======
.sh-season-card {
  background: @surface;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 16px;

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  &__name {
    font-size: var(--vk-fs-base);
    font-weight: 700;
    color: #e2e8f0;
  }

  &__date {
    font-size: var(--vk-fs-2xs);
    color: var(--vk-t3);
    margin-top: 2px;
  }

  &__status {
    font-size: 9px;
    font-weight: 900;
    letter-spacing: 0.1em;
    padding: 4px 10px;
    border-radius: 999px;
    white-space: nowrap;

    &--completed {
      background: fade(@primary, 15%);
      color: @primary;
      border: 1px solid fade(@primary, 30%);
    }

    &--expired {
      background: rgba(239, 68, 68, 0.1);
      color: #f87171;
      border: 1px solid rgba(239, 68, 68, 0.2);
    }
  }

  &__progress {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  &__bar {
    flex: 1;
    height: 6px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 999px;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    background: @primary;
    border-radius: 999px;
    transition: width 0.5s;
  }

  &__pts {
    font-size: var(--vk-fs-2xs);
    font-weight: 700;
    color: var(--vk-t2);
    white-space: nowrap;
  }

  &__rewards {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
  }

  &__reward {
    display: flex;
    align-items: center;
    gap: 4px;

    span {
      font-size: var(--vk-fs-xs);
      font-weight: 700;
      color: var(--vk-t2);
    }
  }

  &__total {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 4px;

    span {
      font-size: var(--vk-fs-sm);
      font-weight: 900;
      color: @gold;
    }
  }
}

// ====== MINI ICONS ======
.sh-coin-mini {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FFF720, #FFD700, #B8860B);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset -1px -1px 2px rgba(0, 0, 0, 0.4), 1px 2px 4px rgba(0, 0, 0, 0.3);
  flex-shrink: 0;

  span {
    font-size: var(--vk-fs-2xs);
    font-weight: 900;
    color: #5c4a00;
  }
}

.sh-shield-mini {
  width: 20px;
  height: 20px;
  position: relative;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    inset: 2px;
    background: linear-gradient(135deg, #FFF720, #FFD700, #B8860B);
    border-radius: 2px;
    transform: rotate(45deg);
    box-shadow: inset -1px -1px 2px rgba(0, 0, 0, 0.5), 1px 2px 3px rgba(0, 0, 0, 0.4);
  }
}

.sh-chest-mini {
  width: 20px;
  height: 14px;
  background: linear-gradient(to bottom, #4d3a2b, #2a1b12);
  border-radius: 1px;
  position: relative;
  box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.5);
  border-bottom: 1px solid rgba(0, 0, 0, 0.6);
  flex-shrink: 0;

  &__lid {
    position: absolute;
    top: -5px;
    left: 0;
    width: 100%;
    height: 7px;
    background: linear-gradient(to top, #5c4033, #8b5e3c);
    border-radius: 4px 4px 0 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.4);
  }

  &__lock {
    position: absolute;
    top: 1px;
    left: 50%;
    transform: translateX(-50%);
    width: 5px;
    height: 6px;
    background: linear-gradient(135deg, #ffb020, #B8860B);
    border-radius: 1px;
  }
}

// ====== EMPTY ======
.sh-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 0;

  p {
    color: var(--vk-t3);
    font-size: var(--vk-fs-base);
  }
}

// ====== RESPONSIVE ======
@media (min-width: 769px) {
  .sh-page {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
  }
}
</style>
